import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { getBranding, getPoweredByText } from "../utils/branding";

/**
 * StatusScreen — the single, shared full-screen status card for EVERY exceptional/terminal state in
 * the registration flow (link-not-found, deactivated, seats-full, sponsor-exhausted, invitation-only,
 * pmt errors, payment-failed, duplicate, generic error, and processing/loading). One consistent look
 * replaces the ~11 hand-rolled inline blocks that were inconsistent (and sometimes showed a non-success
 * state in a green "success" card).
 *
 * Props:
 *  - variant: 'error' | 'warning' | 'info' | 'success' | 'loading'   (drives color + default icon)
 *  - title:   string
 *  - message: string | node
 *  - icon:    optional MUI icon element (else a sensible default per variant)
 *  - actions: optional [{ label, onClick, primary?, href? }]  → buttons
 *  - details: optional string/node → a tinted helper/tip box under the message
 *  - eventId / logoUrl / eventName: optional → show the event logo + name at the top
 *  - autoReloadSeconds: optional number → shows a small countdown + spinner (used by Payment Failed)
 */

const VARIANTS = {
  error: {
    color: "#ef4444",
    dark: "#dc2626",
    softBg: "#fef2f2",
    softBorder: "#fecaca",
    Icon: ErrorOutlineRoundedIcon,
  },
  warning: {
    color: "#f59e0b",
    dark: "#d97706",
    softBg: "#fffbeb",
    softBorder: "#fde68a",
    Icon: WarningAmberRoundedIcon,
  },
  info: {
    color: "#0ea5e9",
    dark: "#0284c7",
    softBg: "#f0f9ff",
    softBorder: "#bae6fd",
    Icon: InfoRoundedIcon,
  },
  success: {
    color: "#10b981",
    dark: "#059669",
    softBg: "#ecfdf5",
    softBorder: "#a7f3d0",
    Icon: CheckCircleRoundedIcon,
  },
  loading: {
    color: "#2563eb",
    dark: "#1e40af",
    softBg: "#eff6ff",
    softBorder: "#bfdbfe",
    Icon: InfoRoundedIcon,
  },
};

const BRAND_GRADIENT = "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)";

const resolveLogo = (eventId, logoUrl) => {
  if (logoUrl) return logoUrl;
  if (eventId) {
    if (eventId === "1745879876")
      return "https://api-admin.eventegration.com/public/logo/1745879876.png?t=1766999775406";
    return `https://api-reg.eventegration.com/images/logo/${eventId}.png`;
  }
  return null;
};

function Countdown({ seconds, color }) {
  const [left, setLeft] = React.useState(seconds);
  React.useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25, mt: 1 }}>
      <CircularProgress size={18} thickness={5} sx={{ color }} />
      <Typography variant="body2" sx={{ color: "#6b7280" }}>
        Refreshing in {Math.max(0, left)}s…
      </Typography>
    </Box>
  );
}

export default function StatusScreen({
  variant = "error",
  title,
  message,
  icon,
  actions = [],
  details,
  eventId,
  logoUrl,
  eventName,
  autoReloadSeconds,
}) {
  const v = VARIANTS[variant] || VARIANTS.error;
  const DefaultIcon = v.Icon;
  const logo = resolveLogo(eventId, logoUrl);
  const branding = getBranding(eventId);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        px: 2,
        py: 6,
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <Box
            sx={{
              position: "relative",
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 16px 48px rgba(15, 23, 42, 0.12)",
              border: "1px solid #eef2f7",
            }}
          >
            {/* Top accent bar (variant colored) */}
            <Box
              sx={{
                height: 5,
                background: `linear-gradient(90deg, ${v.color} 0%, ${v.dark} 100%)`,
              }}
            />

            <Box sx={{ px: { xs: 3, sm: 4 }, pt: 4, pb: 4, textAlign: "center" }}>
              {/* Event logo (optional) */}
              {logo && (
                <Avatar
                  src={logo}
                  alt=""
                  sx={{
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    border: "3px solid #fff",
                  }}
                  imgProps={{ onError: (e) => (e.target.style.display = "none") }}
                />
              )}

              {/* Icon badge */}
              <motion.div
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
              >
                <Box
                  sx={{
                    width: 76,
                    height: 76,
                    mx: "auto",
                    mb: 2.5,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: v.softBg,
                    border: `1px solid ${v.softBorder}`,
                  }}
                >
                  {variant === "loading" ? (
                    <CircularProgress size={38} thickness={4} sx={{ color: v.color }} />
                  ) : icon ? (
                    React.cloneElement(icon, {
                      sx: { fontSize: 42, color: v.color, ...(icon.props?.sx || {}) },
                    })
                  ) : (
                    <DefaultIcon sx={{ fontSize: 42, color: v.color }} />
                  )}
                </Box>
              </motion.div>

              {/* Title */}
              {title && (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    mb: 1,
                    color: "#111827",
                  }}
                >
                  {title}
                </Typography>
              )}

              {/* Event name (optional, small) */}
              {eventName && (
                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 1.25, fontWeight: 600 }}>
                  {eventName}
                </Typography>
              )}

              {/* Message */}
              {message && (
                <Typography
                  variant="body1"
                  sx={{ color: "#6b7280", lineHeight: 1.6, mb: details ? 2 : 3 }}
                >
                  {message}
                </Typography>
              )}

              {/* Details / tip box */}
              {details && (
                <Box
                  sx={{
                    mb: 3,
                    px: 2,
                    py: 1.5,
                    borderRadius: "12px",
                    backgroundColor: v.softBg,
                    border: `1px solid ${v.softBorder}`,
                    textAlign: "left",
                  }}
                >
                  <Typography variant="body2" sx={{ color: v.dark, fontWeight: 500 }}>
                    {details}
                  </Typography>
                </Box>
              )}

              {/* Auto-reload countdown (optional) */}
              {autoReloadSeconds ? <Countdown seconds={autoReloadSeconds} color={v.color} /> : null}

              {/* Actions */}
              {actions.length > 0 && (
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.25,
                    justifyContent: "center",
                  }}
                >
                  {actions.map((a, i) =>
                    a.primary ? (
                      <Button
                        key={i}
                        variant="contained"
                        size="large"
                        href={a.href}
                        onClick={a.onClick}
                        sx={{
                          borderRadius: "12px",
                          px: 3,
                          textTransform: "none",
                          fontWeight: 600,
                          background: BRAND_GRADIENT,
                          boxShadow: "0 6px 18px rgba(30, 58, 138, 0.28)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
                            boxShadow: "0 10px 26px rgba(30, 58, 138, 0.36)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        {a.label}
                      </Button>
                    ) : (
                      <Button
                        key={i}
                        variant="outlined"
                        size="large"
                        href={a.href}
                        onClick={a.onClick}
                        sx={{
                          borderRadius: "12px",
                          px: 3,
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: "#cbd5e1",
                          color: "#475569",
                          "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
                        }}
                      >
                        {a.label}
                      </Button>
                    )
                  )}
                </Box>
              )}
            </Box>

            {/* Footer — powered by */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: "1px solid #f1f5f9",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: "#cbd5e1", fontWeight: 500 }}>
                {getPoweredByText(eventId)}
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
